import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { logout } from '@/redux/auth/authSlice';
import config from '../../../config.json';
import { refreshAccessToken } from '../../redux/auth/authUtils';
import { persistor, RootState, store } from '../../redux/store';
import { globalErrorHandler } from './globalErrorHandler';

const api = axios.create({
  baseURL: config.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { accessToken, user } = (store.getState() as RootState).auth;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      config.headers.posid = user?.devicePOS?.ID;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if the error is due to an expired token (status 401)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { refreshToken } = (store.getState() as RootState).auth;

      // Only try to refresh if we have a refresh token
      if (refreshToken) {
        try {
          // Refresh the access token
          const newAccessToken = await refreshAccessToken(store.dispatch, store.getState);

          // Update the Authorization header with the new access token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Retry the original request with the new access token
          return api(originalRequest);
        } catch (refreshError) {
          // If token refresh fails, log the user out
          persistor.purge();
          store.dispatch(logout());
          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available, log out and redirect to login
        persistor.purge();
        store.dispatch(logout());
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
    // if (axios.isAxiosError(error) && error.response) {
    //   return Promise.reject(error.response.data);
    // }
    if (axios.isAxiosError(error)) {
      globalErrorHandler(error);
    }
    return Promise.reject(error);
  }
);

export default api;
